import type { RecordsStepProps } from '../../congratulations-full-screen.types';
import { faCheck, faFire } from '@fortawesome/free-solid-svg-icons';
import { faCrosshairs, faFlag, faListCheck, faRoute } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Difficulty, StyledFlex, SwitchThemeContext, theme } from '@tryhackme/thm-ui-components';
import React, { Suspense, useState, useContext } from 'react';
import { useAppSelector } from 'src/app/hooks';
import { StyledLoading } from 'src/common/components';
import { ThemeColorMode } from 'src/common/enums';
import { CampaignName } from 'src/common/enums/campaigns';
import { useGetUserQuery } from 'src/common/slices';
import { FireshipTopBar } from 'src/features/campaigns/fireship/fireship-top-bar';
import { useCampaignCheck } from 'src/features/campaigns/hooks/use-campaign-check';
import { useGetUserRoomScoreQuery } from 'src/features/room/room.slice';
import { useGetUserLeagueEligibilityQuery } from 'src/features/weekly-leagues/weekly-leagues.slice';
import { StyledSentFeedbackMsg } from '../../../completion-modal/completion-modal.styles';
import { StyledContentWrapper, StyledTitle } from '../../congratulations-full-screen.styles';
import { ReactComponent as LeagueIcon } from './league-icon.svg';
import { RecordItem } from './record-item';
import { StyledAvatar, StyledCircleCheck, StyledLeagueContent, StyledLeagueIcon, StyledLeagueNotification, StyledLeagueTitle, StyledRedeemTicketsWrapper, StyledStreakRepairIcon, StyledStreakRepairText } from './records-step.styles';
const getLeagueMessage = (completedRooms: number) => {
  if (completedRooms === 1) {
    return 'This room counted toward joining the league 🎯';
  }
  if (completedRooms === 2) {
    return "You've unlocked League access! Ready to compete? 🏆";
  }
  return '';
};
export const RecordsStep = ({
  currentRoom,
  isLoading,
  isFeedbackSent
}: RecordsStepProps) => {
  const [awardTicketsToUser, setAwardTicketsToUser] = useState(true);
  const isStreakRepaired = useAppSelector(state => state.room.isStreakRepaired);
  const {
    theme: themeContext
  } = useContext(SwitchThemeContext);
  const isDarkMode = themeContext === ThemeColorMode.DARK;
  const {
    nextRoom
  } = currentRoom || {};
  const roomCode = currentRoom?.code ?? '';
  const hasEventTickets = currentRoom?.events?.tickets;
  const isTeamRoom = currentRoom?.teamSettings?.enabled;
  const {
    data: userData
  } = useGetUserQuery();
  const {
    data: userRoomScoreData,
    isLoading: userRoomScoreLoading
  } = useGetUserRoomScoreQuery(roomCode);
  const isInFireshipCampaign = useCampaignCheck([CampaignName.FIRESHIP_2025]);
  const {
    data: userLeagueEligibilityData
  } = useGetUserLeagueEligibilityQuery(undefined, {
    skip: !userData?.data?.user
  });
  // if experiment passed, we will create a new endpoint
  const userName = userData?.data?.user.username;
  const userScore = userRoomScoreData?.data?.totalScore;
  const userTotalTasks = userRoomScoreData?.data?.totalTasks;
  const isUserSubscribed = userData?.data?.user?.isPremium;
  const userCompletedRooms = userLeagueEligibilityData?.data?.userCompletedRooms || 0;
  const RedeemTickets = React.lazy(() => import('../../../completion-modal/components/redeem-tickets').then(module => ({
    default: module.RedeemTickets
  })));
  return <StyledContentWrapper data-sentry-element="StyledContentWrapper" data-sentry-component="RecordsStep" data-sentry-source-file="records-step.tsx">
      <StyledAvatar data-sentry-element="StyledAvatar" data-sentry-source-file="records-step.tsx">
        <Avatar hasBorder avatarSrc={currentRoom?.imageURL ?? ''} size="20rem" mobileSize="15rem" borderWidth="6px" borderColor={theme.colors.primary.main} data-sentry-element="Avatar" data-sentry-source-file="records-step.tsx" />
        <StyledCircleCheck data-sentry-element="StyledCircleCheck" data-sentry-source-file="records-step.tsx">
          <FontAwesomeIcon icon={faCheck} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="records-step.tsx" />
        </StyledCircleCheck>
      </StyledAvatar>
      <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="records-step.tsx">{`Congratulations on completing ${currentRoom?.title ?? ''}!!! 🎉`}</StyledTitle>
      {isLoading || userRoomScoreLoading ? <StyledFlex justifyContent="center">
          <StyledLoading width="16rem" height="9.4rem" />
          <StyledLoading width="16rem" height="9.4rem" />
          <StyledLoading width="16rem" height="9.4rem" />
          <StyledLoading width="16rem" height="9.4rem" />
          <StyledLoading width="16rem" height="9.4rem" />
        </StyledFlex> : <>
          <StyledFlex justifyContent="center">
            {isTeamRoom ? null : <>
                <RecordItem title="Points earned" icon={faCrosshairs} content={userScore} recordType="points" />
                <RecordItem title="Completed tasks" icon={faListCheck} content={userTotalTasks} recordType="tasks" />
              </>}

            <RecordItem title="Room type" icon={currentRoom?.type === 'walkthrough' ? faRoute : faFlag} content={currentRoom?.type} recordType="room-type" />
            <RecordItem title="Difficulty" content={<Difficulty difficulty={currentRoom?.difficulty ?? 'easy'} isDarkVersion hasIcon fontSize="2.4rem" />} recordType="streak" />
            <RecordItem title="Streak" icon={faFire} content={userData?.data?.user?.streak?.streak} recordType="streak" />
          </StyledFlex>

          {userLeagueEligibilityData?.data?.hasMetCriteria && <StyledLeagueNotification>
              <StyledLeagueIcon>
                <LeagueIcon />
              </StyledLeagueIcon>
              <StyledLeagueContent>
                <StyledLeagueTitle>{getLeagueMessage(userCompletedRooms)}</StyledLeagueTitle>
              </StyledLeagueContent>
            </StyledLeagueNotification>}

          {isInFireshipCampaign && <FireshipTopBar embedded />}

          {isStreakRepaired && <StyledFlex justifyContent="center" gap="1.4rem" alignItems="flex-start">
              <StyledStreakRepairIcon $isDarkMode={isDarkMode} />
              <StyledStreakRepairText $isDarkMode={isDarkMode}>
                <p>You successfully repaired your {userData?.data?.user?.streak?.streak} day streak! </p>
                <span>That was close! Keep learning every day to grow your streak!</span>
              </StyledStreakRepairText>
            </StyledFlex>}

          {isFeedbackSent && <StyledSentFeedbackMsg>
              <FontAwesomeIcon icon={faCheck} /> <p>Sent! Thank you for your feedback!</p>
            </StyledSentFeedbackMsg>}
        </>}
      <Suspense fallback={<StyledLoading height="20rem" />} data-sentry-element="Suspense" data-sentry-source-file="records-step.tsx">
        {hasEventTickets && awardTicketsToUser && <StyledRedeemTicketsWrapper>
            <RedeemTickets username={userName ?? ''} isUserSubscribed={isUserSubscribed || false} isPathway={Boolean(nextRoom)} roomCode={currentRoom?.code ?? ''} setAwardTicketsToUser={setAwardTicketsToUser} />
          </StyledRedeemTicketsWrapper>}
      </Suspense>
    </StyledContentWrapper>;
};